"use client";

import Link from "next/link";
import { useRouter, useSearchParams } from "next/navigation";
import { BellIcon } from "@/components/icons";
import { InboxCountBadge } from "@/components/monitoring/InboxCountBadge";
import { useInboxCount } from "@/hooks/useInboxCount";
import type { SidebarFolder, SidebarWorker } from "./types";
import { MonitoringFolderTree } from "./MonitoringFolderTree";

/** Outcome folders scope the current Monitoring view. */
export function MonitoringNav({
  activeBusiness,
  folders,
  workers = [],
  pathname,
  businessInboxCount = null,
  visible = true,
  onCloseDrawer,
  onChanged,
}: {
  activeBusiness: string;
  folders: SidebarFolder[];
  workers?: SidebarWorker[];
  pathname: string;
  /** The whole business's Inbox count, polled once by the sidebar; shown while no department is selected. */
  businessInboxCount?: number | null;
  /** Whether the rail is on screen; a department's count is polled only while it is. */
  visible?: boolean;
  onCloseDrawer: () => void;
  onChanged?: () => Promise<void>;
}) {
  const router = useRouter();
  const searchParams = useSearchParams();
  const businessWide = searchParams.get("view") === "reports" || Boolean(searchParams.get("report")) || searchParams.get("tab") === "logs";
  const selectedDepartmentId = searchParams.get("dept") || null;
  const departmentId = businessWide ? null : selectedDepartmentId;
  // The Inbox row keeps the selected department through Reports and Logs, exactly as the page's tab strip does,
  // so the two Inbox links on one screen open the same scope and carry the same number.
  // Only a department scope is polled here; the business-wide count already comes from the sidebar.
  const departmentInbox = useInboxCount(selectedDepartmentId, visible && Boolean(selectedDepartmentId));
  const inboxCount = selectedDepartmentId ? departmentInbox.count : businessInboxCount;
  const inboxActive = pathname === "/monitoring/inbox";
  const inboxHref = `/monitoring/inbox${selectedDepartmentId ? `?${new URLSearchParams({ dept: selectedDepartmentId })}` : ""}`;

  function departmentHref(id: string | null) {
    const next = businessWide ? new URLSearchParams() : new URLSearchParams(searchParams.toString());
    if (id) next.set("dept", id);
    else next.delete("dept");
    const path = businessWide ? "/monitoring" : pathname;
    return `${path}${next.size ? `?${next}` : ""}`;
  }

  return (
    <nav aria-label="Monitoring scope" className="flex min-h-0 flex-1 flex-col gap-4 overflow-hidden px-2 pb-4 pt-1">
      <div className="flex min-h-0 flex-1 flex-col overflow-hidden">
        <MonitoringFolderTree key={activeBusiness} businessId={activeBusiness} folders={folders} workers={workers} pathname={pathname} selectedId={departmentId}
          hrefFor={departmentHref} onCloseDrawer={onCloseDrawer} onChanged={onChanged}
          onDeleted={folder => { if (departmentId === folder.id) router.replace(departmentHref(folder.parentId)); }} />
        {businessWide && <p className="shrink-0 px-3 pt-2 text-xs leading-relaxed text-zinc-500">Reports and logs cover the entire business. Choose a folder to open its dashboard.</p>}
      </div>
      <div className="shrink-0 border-t border-zinc-800 pt-2">
        <Link href={inboxHref} onClick={onCloseDrawer} aria-current={inboxActive ? "page" : undefined}
          className={`flex h-9 min-w-0 items-center gap-1.5 rounded-lg pl-7 pr-3 text-sm font-medium transition focus-visible:outline-2 focus-visible:outline-violet-400 focus-visible:outline-offset-2 ${inboxActive ? "bg-violet-500/10 font-medium text-violet-300" : "text-zinc-300 hover:bg-zinc-800/60 hover:text-zinc-100"}`}>
          <BellIcon className="size-3.5 shrink-0 text-zinc-500" />
          <span className="min-w-0 flex-1">Inbox</span>
          <InboxCountBadge count={inboxCount} />
        </Link>
      </div>
    </nav>
  );
}
